/**
 * Sound and touch feedback for Kid Mode.
 *
 * A two-year-old reads a screen through its reactions, not its labels: a tap
 * that answers back is understood, a tap that goes quiet reads as broken. So
 * every touch gets a short warm tone and a short buzz.
 *
 * Tones are synthesised rather than loaded — no asset to fetch, nothing to
 * fail offline, and a soft envelope on every note so nothing ever clicks or
 * startles. Everything here degrades to doing nothing: audio is blocked until
 * the first gesture on iOS, and most devices have no vibration motor at all.
 */

export type Cue = 'tap' | 'good' | 'again' | 'celebrate' | 'unlock'

const MUTE_KEY = 'aurora:kidMuted'

let ctx: AudioContext | null = null
let muted = readMuted()

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    return false
  }
}

export function isMuted(): boolean {
  return muted
}

export function setMuted(value: boolean) {
  muted = value
  try {
    localStorage.setItem(MUTE_KEY, value ? '1' : '0')
  } catch {
    /* private mode — the choice just will not persist */
  }
}

function audio(): AudioContext | null {
  if (muted) return null
  try {
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as any).webkitAudioContext
    if (!Ctor) return null
    ctx ??= new Ctor()
    // Autoplay policy suspends the context until a real gesture; every cue
    // follows one, so asking again here is what actually wakes it.
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

/**
 * A pentatonic set — any two of these sound good together, so overlapping
 * taps never produce a sour interval however fast she presses.
 */
const NOTES = { C: 523.25, D: 587.33, E: 659.25, G: 783.99, A: 880.0, C2: 1046.5 }

const CUES: Record<Cue, Array<{ hz: number; at: number; ms: number; gain: number }>> = {
  tap: [{ hz: NOTES.E, at: 0, ms: 90, gain: 0.16 }],
  good: [
    { hz: NOTES.E, at: 0, ms: 110, gain: 0.18 },
    { hz: NOTES.G, at: 80, ms: 140, gain: 0.18 },
  ],
  /** Not a buzzer. A soft step down that says "have another look". */
  again: [{ hz: NOTES.D, at: 0, ms: 150, gain: 0.13 }],
  celebrate: [
    { hz: NOTES.C, at: 0, ms: 150, gain: 0.2 },
    { hz: NOTES.E, at: 100, ms: 150, gain: 0.2 },
    { hz: NOTES.G, at: 200, ms: 180, gain: 0.2 },
    { hz: NOTES.C2, at: 320, ms: 420, gain: 0.22 },
  ],
  unlock: [{ hz: NOTES.A, at: 0, ms: 70, gain: 0.1 }],
}

const BUZZ: Record<Cue, number | number[]> = {
  tap: 10,
  good: 18,
  again: [8, 40, 8],
  celebrate: [0, 30, 60, 30, 60, 70],
  unlock: 24,
}

function tone(hz: number, delayMs: number, ms: number, gain: number) {
  const ac = audio()
  if (!ac) return
  const start = ac.currentTime + delayMs / 1000
  const end = start + ms / 1000

  const osc = ac.createOscillator()
  const amp = ac.createGain()
  osc.type = 'triangle'
  osc.frequency.value = hz

  // Ramped in and out — a square-edged envelope is what makes synth tones
  // sound cheap and makes them click on small speakers.
  amp.gain.setValueAtTime(0.0001, start)
  amp.gain.exponentialRampToValueAtTime(gain, start + 0.012)
  amp.gain.exponentialRampToValueAtTime(0.0001, end)

  osc.connect(amp).connect(ac.destination)
  osc.start(start)
  osc.stop(end + 0.02)
}

/** Play a cue. Safe to call on every touch; safe to call where neither works. */
export function play(cue: Cue) {
  for (const note of CUES[cue]) tone(note.hz, note.at, note.ms, note.gain)
  buzz(cue)
}

export function buzz(cue: Cue) {
  try {
    navigator.vibrate?.(BUZZ[cue])
  } catch {
    /* no motor, or the page is not visible — nothing to fall back to */
  }
}

/** Called on the first gesture of a session so later cues are not swallowed. */
export function warmUp() {
  audio()
}
